import React from 'react';
import { Link } from '../ui';
import { EmailFormState, EmailSendDTO, EmailTemplate, Placeholder, PreviewRow } from '../../store/email/types';
import { GridColumnProps } from '@progress/kendo-react-grid';
import ActionsCell from '../ActionsCell/ActionsCell';
import { getEmailRender } from '../../store/email/sagas';
import Loading from '../Loading/Loading';
import EmailDialog from '../dialogs/EmailDialog/EmailDialog';
import EmailForm, { EmailFormFields } from '../EmailForm/EmailForm';
import MyGridCore from '../MyGridCore/MyGridCore';
import { DEFAULT_FONT_SIZE } from '../MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import { ValidatedField } from '../../store/api.d';
import EmailSending from '../EmailSending/EmailSending';
import { Role } from '../../store/conference/types';

interface Props {
  placeholders: Placeholder[];
  formState: EmailFormState;
  preview?: PreviewRow[];
  email: EmailSendDTO;
  onChange: (value: EmailFormFields) => void;
  templatesById: { [key: number]: EmailTemplate };
  validationError?: ValidatedField[];
  role: Role | null;
}

const SendEmailForm: React.FC<Props> = ({
  placeholders,
  formState,
  preview,
  email,
  onChange,
  templatesById,
  validationError,
  role,
}) => {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = React.useState<JSX.Element | undefined>(undefined);

  switch (formState) {
    case 'COMPOSE':
      return (
        <EmailForm
          onChange={onChange}
          placeholders={placeholders}
          value={email}
          templatesById={templatesById}
          validationErrors={validationError}
          hideTemplates={role?.type != 'chair'}
        />
      );
    case 'PREVIEW':
      const gridColumnProps: GridColumnProps[] = [];
      gridColumnProps.push(
        {
          field: 'recipient_name',
          title: t('Recipient name'),
        },
        {
          field: 'recipient_address',
          title: t('Recipient address'),
        },
        {
          field: 'subject',
          title: t('Subject'),
        },
        {
          field: 'body',
          title: t('Body'),
          cell: ActionsCell,
          filterable: false,
        },
      );

      const inputData: any[] = preview
        ? preview.map((previewRow, index) => {
            const { id, recipient_ids, ...rest } = previewRow;
            return {
              id: index,
              ...rest,
              body: (
                <Link
                  className="flex items-center cursor-pointer font-semibold"
                  onClick={async () => {
                    const response = await getEmailRender({
                      id,
                      subject: email.subject,
                      body: email.body,
                      recipient_type: email.recipient_type,
                      recipient_ids: recipient_ids,
                    });
                    if (response.type === 'ok') {
                      setDialog(
                        <EmailDialog
                          show={true}
                          onClose={() => setDialog(undefined)}
                          html={response.value.data.html}
                        />,
                      );
                    }
                  }}
                >
                  {t('Preview')}
                </Link>
              ),
            };
          })
        : [];

      return (
        <div>
          <div className="mb-2">
            {inputData.length >= 20 && <span>Preview of the first {inputData.length} emails to be sent.</span>}
          </div>
          <MyGridCore
            allGridData={inputData}
            gridSettings={{
              gridColumnProps: gridColumnProps,
              advancedFilter: false,
              DATA_ITEM_KEY: 'id',
              SELECTED_FIELD: '',
              LABELABLE_ITEM_KEY: '',
              fontSize: DEFAULT_FONT_SIZE,
              dataState: {},
            }}
            selectedState={{}}
            dateFormat={null}
            selectable={false}
            showActionsColumn={false}
            showFilters={true}
          />
          {dialog}
        </div>
      );
    case 'SENDING':
      return <EmailSending token={email.token ?? ''} />;
    case 'WAIT':
      return (
        <div className="m-5">
          <Loading compact={true} />
        </div>
      );
  }
};

export default SendEmailForm;
